import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
} from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import api from "../../api/api";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import blogData from "../../data/blog.json";
import blogDeaultImage from "../../image/default blog.png";

import {
  Clock,
  Eye,
  Heart,
  Tag,
  ArrowRight,
  Search,
  X,
  SlidersHorizontal,
  TrendingUp,
  ChevronRight,
  BookOpen,
  User,
} from "lucide-react";

/* ─────────────────────────────────────────────
 CATEGORY COLORS
───────────────────────────────────────────── */
const CAT_COLORS = {
  Research: "#3b82f6",
  "Climate Change": "#10b981",
  Migration: "#6366f1",
  Gender: "#ec4899",
  Governance: "#f59e0b",
  "M&E": "#14b8a6",
  "Capacity Building": "#8b5cf6",
  default: "#3b82f6",
};

const getCatColor = (cat) => CAT_COLORS[cat] || CAT_COLORS.default;
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/* ─────────────────────────────────────────────
 BLOG CARD (standard)
───────────────────────────────────────────── */
const BlogCard = ({ post, index, layout = "grid" }) => {
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const lineRef = useRef(null);
  const arrowRef = useRef(null);
  const glowRef = useRef(null);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(post.likes);
  const color = getCatColor(post.category);

  /* Scroll reveal */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { y: 55, opacity: 0, scale: 0.96 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.75,
          delay: (index % 3) * 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 87%",
          },
        },
      );
    }, cardRef);
    return () => ctx.revert();
  }, [index]);

  const handleEnter = useCallback(() => {
    gsap.to(imgRef.current, {
      scale: 1.07,
      duration: 0.6,
      ease: "power2.out",
    });
    gsap.to(lineRef.current, {
      scaleX: 1,
      duration: 0.4,
      ease: "power3.out",
    });
    gsap.to(glowRef.current, {
      opacity: 1,
      duration: 0.4,
    });
    gsap.to(arrowRef.current, {
      x: 4,
      opacity: 1,
      duration: 0.3,
      ease: "power2.out",
    });
  }, []);

  const handleLeave = useCallback(() => {
    gsap.to(imgRef.current, {
      scale: 1,
      duration: 0.5,
      ease: "power2.out",
    });
    gsap.to(lineRef.current, {
      scaleX: 0,
      duration: 0.3,
      ease: "power2.in",
    });
    gsap.to(glowRef.current, {
      opacity: 0,
      duration: 0.3,
    });
    gsap.to(arrowRef.current, {
      x: 0,
      opacity: 0.4,
      duration: 0.3,
    });
  }, []);

  const handleLike = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setLiked((prev) => !prev);
      setLikes((prev) => (liked ? prev - 1 : prev + 1));

      gsap.fromTo(
        e.currentTarget,
        { scale: 0.8 },
        { scale: 1, duration: 0.4, ease: "back.out(2)" },
      );
    },
    [liked],
  );

  const isListLayout = layout === "list";

  return (
    <article
      ref={cardRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className={`group relative rounded-2xl overflow-hidden cursor-pointer
 transition-shadow duration-300 hover:shadow-card-hover
 ${isListLayout ? "flex gap-0" : "flex flex-col"}`}
      style={{
        opacity: 0,
        background: "rgba(255,255,255,0.8)",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {/* Glow */}
      <div
        ref={glowRef}
        className="absolute inset-0 pointer-events-none rounded-2xl z-0"
        style={{
          background: `radial-gradient(ellipse 70% 50% at 50% 0%,
 ${color}10 0%, transparent 60%)`,
          opacity: 0,
        }}
      />

      {/* Image */}
      <div
        className={`relative overflow-hidden flex-shrink-0
 ${isListLayout ? "w-48 sm:w-56" : "w-full"}`}
        style={{ aspectRatio: isListLayout ? "1/1" : "16/9" }}
      >
        <img
          ref={imgRef}
          src={post.image ? `${backendUrl}${post.image}` : blogDeaultImage}
          alt={post.title}
          className="w-full h-full object-cover will-change-transform"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 40%, rgba(6,13,36,0.6) 100%)",
          }}
        />
        {/* Category pill on image */}
        <div className="absolute top-3 left-3">
          <span
            className="text-[10px] font-bold tracking-wider uppercase
 px-2.5 py-1 rounded-full"
            style={{
              color,
              background: `${color}20`,
              border: `1px solid ${color}40`,
            }}
          >
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col flex-1 p-5 space-y-3">
        {/* Meta top row */}
        <div className="flex items-center gap-3 text-[11px] text-gray-600">
          <span className="flex items-center gap-1">
            <User size={10} className="text-primary-400" />
            {post.author.name}
          </span>
          <span className="w-px h-3 bg-gray-300" />
          <span className="flex items-center gap-1">
            <Clock size={10} />
            {post.readTime}
          </span>
          <span className="w-px h-3 bg-gray-300" />
          <time dateTime={post.dateISO} className="flex items-center gap-1">
            {post.date}
          </time>
        </div>

        {/* Title */}
        <h3
          className="font-bold text-gray-900 leading-snug
 group-hover:text-primary-600 transition-colors duration-300"
          style={{ fontSize: isListLayout ? "1rem" : "1.05rem" }}
        >
          {post.title}
        </h3>

        {/* Animated underline */}
        <div
          ref={lineRef}
          className="h-px rounded-full origin-left"
          style={{
            background: `linear-gradient(to right, ${color}50, transparent)`,
            transform: "scaleX(0)",
          }}
        />

        {/* Excerpt (grid only) */}
        {!isListLayout && (
          <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 flex-1">
            {post.excerpt}
          </p>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {post.tags.slice(0, isListLayout ? 2 : 3).map((tag, i) => (
            <span key={i} className="tag text-[10px] py-0.5 px-2">
              <Tag size={8} />
              {tag}
            </span>
          ))}
        </div>

        {/* Bottom row */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <Eye size={11} className="text-primary-400" />
              {post.views}
            </span>
            <button
              onClick={handleLike}
              className="flex items-center gap-1 transition-colors duration-300
 hover:text-secondary-400"
              style={{ color: liked ? "#f59e0b" : undefined }}
              aria-label="Like"
            >
              <Heart
                size={11}
                fill={liked ? "#f59e0b" : "transparent"}
                stroke={liked ? "#f59e0b" : "currentColor"}
              />
              {likes}
            </button>
          </div>

          <Link
            to={post.href}
            className="flex items-center gap-1.5 text-xs font-semibold
 transition-colors duration-300"
            style={{ color }}
          >
            <span>Read</span>
            <div ref={arrowRef} style={{ opacity: 0.4 }}>
              <ArrowRight size={12} />
            </div>
          </Link>
        </div>
      </div>
    </article>
  );
};

/* ─────────────────────────────────────────────
 SEARCH BAR
───────────────────────────────────────────── */
const SearchBar = ({ value, onChange, onClear }) => {
  const ref = useRef(null);
  const lineRef = useRef(null);

  const handleFocus = () => {
    gsap.to(lineRef.current, {
      scaleX: 1,
      duration: 0.4,
      ease: "power3.out",
    });
    gsap.to(ref.current, {
      borderColor: "rgba(29,78,216,0.5)",
      duration: 0.3,
    });
  };

  const handleBlur = () => {
    if (!value) {
      gsap.to(lineRef.current, {
        scaleX: 0,
        duration: 0.3,
        ease: "power2.in",
      });
    }
    gsap.to(ref.current, {
      borderColor: "rgba(0,0,0,0.08)",
      duration: 0.3,
    });
  };

  return (
    <div
      ref={ref}
      className="relative flex items-center rounded-xl overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.8)",
        border: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Search
        size={16}
        className="absolute left-4 text-gray-500 pointer-events-none"
      />
      <input
        type="text"
        placeholder="Search articles..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="w-full bg-transparent pl-10 pr-10 py-3 text-sm
 text-gray-900 placeholder-gray-500 outline-none"
      />
      {value && (
        <button
          onClick={onClear}
          className="absolute right-3 text-gray-500 hover:text-gray-900
 transition-colors duration-300"
        >
          <X size={14} />
        </button>
      )}
      {/* Focus line */}
      <div
        ref={lineRef}
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
        style={{
          background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
          transform: "scaleX(0)",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 CATEGORY FILTER BAR
───────────────────────────────────────────── */
const CategoryBar = ({ categories, active, onChange }) => {
  const indicatorRef = useRef(null);
  const activeRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!activeRef.current || !indicatorRef.current || !containerRef.current)
      return;
    const aRect = activeRef.current.getBoundingClientRect();
    const cRect = containerRef.current.getBoundingClientRect();
    gsap.to(indicatorRef.current, {
      x: aRect.left - cRect.left,
      width: aRect.width,
      duration: 0.4,
      ease: "power3.out",
    });
  }, [active]);

  return (
    <div
      ref={containerRef}
      className="relative flex items-center gap-1 flex-wrap"
    >
      {/* Sliding bg indicator */}
      <div
        ref={indicatorRef}
        className="absolute h-8 rounded-lg bg-primary-600/20
 border border-primary-500/30 pointer-events-none"
        style={{ width: 0 }}
      />

      {categories.map((cat) => {
        const isActive = cat === active;
        return (
          <button
            key={cat}
            ref={isActive ? activeRef : null}
            onClick={() => onChange(cat)}
            className="relative px-4 py-1.5 text-xs font-semibold rounded-lg
 transition-colors duration-300 z-10"
            style={{
              color: isActive ? getCatColor(cat) : "rgba(75,85,99,0.8)",
            }}
          >
            {cat}
          </button>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────
 LAYOUT TOGGLE
───────────────────────────────────────────── */
const LayoutToggle = ({ layout, onChange }) => (
  <div
    className="flex items-center rounded-xl overflow-hidden"
    style={{
      background: "rgba(255,255,255,0.8)",
      border: "1px solid rgba(0,0,0,0.08)",
    }}
  >
    {["grid", "list"].map((l) => (
      <button
        key={l}
        onClick={() => onChange(l)}
        className="px-4 py-2 text-xs font-semibold transition-all duration-300"
        style={{
          background: layout === l ? "rgba(29,78,216,0.3)" : "transparent",
          color: layout === l ? "#60a5fa" : "rgba(75,85,99,0.6)",
        }}
        aria-label={`${l} layout`}
      >
        {l === "grid" ? (
          <div className="grid grid-cols-2 gap-[3px] w-3.5 h-3.5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-[1px] bg-current" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-[3px] w-3.5 h-3.5 justify-center">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[2px] rounded-full bg-current" />
            ))}
          </div>
        )}
      </button>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
 SIDEBAR
───────────────────────────────────────────── */
const BlogSidebar = ({ sidebar, allPosts }) => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { x: 40, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ref.current,
            start: "top 80%",
          },
        },
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <aside
      ref={ref}
      className="space-y-6 lg:sticky lg:top-28"
      style={{ opacity: 0 }}
    >
      {/* Trending topics */}
      <div
        className="rounded-2xl p-5 space-y-4"
        style={{
          background: "rgba(255,255,255,0.8)",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-secondary-400" />
          <h4 className="text-sm font-bold text-gray-900">Trending Topics</h4>
        </div>
        <div className="space-y-2">
          {sidebar.trendingTopics && sidebar.trendingTopics.length > 0 ? (
            sidebar.trendingTopics.map((topic, i) => (
              <Link
                to={`${topic.href}`}
                key={topic.id}
                className="w-full flex items-center justify-between py-2
 border-b border-gray-200 group last:border-0 hover:bg-blue-100 rounded-lg px-3"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-xs font-mono text-gray-500"
                    style={{ minWidth: "1.5ch" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="text-sm text-gray-700 group-hover:text-gray-900
 transition-colors duration-300 text-left line-clamp-2"
                  >
                    {topic.title}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[10px] text-gray-500"></span>
                  <ChevronRight
                    size={12}
                    className="text-gray-400 group-hover:text-primary-400
 transition-colors duration-300"
                  />
                </div>
              </Link>
            ))
          ) : (
            <p className="text-xs text-gray-500 italic py-1">
              No featured stories trending
            </p>
          )}
        </div>
      </div>

      {/* Popular tags */}
      <div
        className="rounded-2xl p-5 space-y-4"
        style={{
          background: "rgba(255,255,255,0.8)",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-primary-400" />
          <h4 className="text-sm font-bold text-gray-900">Popular Tags</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {sidebar.popularTags && sidebar.popularTags.length > 0 ? (
            sidebar.popularTags.map((tag, i) => (
              <button
                key={i}
                className="tag text-[10px] hover:bg-primary-600/30
 hover:border-primary-500/50 transition-all duration-300"
              >
                {tag}
              </button>
            ))
          ) : (
            <p className="text-xs text-gray-500 italic">No tags listed</p>
          )}
        </div>
      </div>

      {/* Recent posts */}
      <div
        className="rounded-2xl p-5 space-y-4"
        style={{
          background: "rgba(255,255,255,0.8)",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-secondary-400" />
          <h4 className="text-sm font-bold text-gray-900">Recent Posts</h4>
        </div>
        <div className="space-y-3">
          {allPosts.slice(0, 3).map((post) => (
            <Link
              key={post.id}
              to={post.href}
              className="flex items-start gap-3 group"
            >
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src={
                    post.image ? `${backendUrl}${post.image}` : blogDeaultImage
                  }
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105
 transition-transform duration-300"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-xs text-gray-900 font-medium leading-snug
 line-clamp-2 group-hover:text-primary-600
 transition-colors duration-300"
                >
                  {post.title}
                </p>
                <p className="text-[10px] text-gray-500 mt-1">{post.date}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
};

/* ─────────────────────────────────────────────
 MAIN BLOG GRID
───────────────────────────────────────────── */
const BlogGrid = () => {
  const { blog } = blogData;
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [layout, setLayout] = useState("grid");

  // React built-in deferred values avoid component thrashing during text inputs
  const deferredSearch = useDeferredValue(searchQuery);

  const gridRef = useRef(null);
  const headerRef = useRef(null);
  const headRef = useRef(null);
  const toolbarRef = useRef(null);

  /* Fetch Data via TanStack Query connecting directly to the API endpoint queries */
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } =
    useInfiniteQuery({
      queryKey: ["blogs", activeCategory, deferredSearch],
      queryFn: async ({ pageParam = 1 }) => {
        const response = await api.get("/api/blogs/public", {
          params: {
            page: pageParam,
            limit: 6, // Keeps pristine look balancing 2/3 item grids perfectly
            category: activeCategory === "All" ? undefined : activeCategory,
            search: deferredSearch || undefined,
          },
        });
        return response.data;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.currentPage < lastPage.totalPages
          ? lastPage.currentPage + 1
          : undefined;
      },
    });

  // Flat structured collection array matching incoming chunk updates cleanly
  const fetchedPosts = data?.pages.flatMap((page) => page.blogs) || [];
  const totalItemsCount = data?.pages[0]?.totalItems || 0;

  /* DYNAMIC EXTRACTIONS FROM SERVER RESPONSE DATA
     Safely parsing data arrays down while completely matching layout expectations.
  */
  // 1. Unique popular tags aggregated automatically across loaded items
  const serverPopularTags = Array.from(
    new Set(fetchedPosts.flatMap((post) => post.tags || [])),
  ).slice(0, 12);

  // 2. Filter out articles flagged as featured entries to display as Trending rows
  const serverTrendingTopics = fetchedPosts
    .filter((post) => post.is_featured === true || post.is_featured === "true")
    .map((post) => ({
      id: post.id,
      title: post.title,
      count: post.views, // Uses standard custom view formatting (e.g. '1.5k') directly
    }));

  /* Header reveal */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 82%",
        },
      });
      tl.fromTo(
        headRef.current?.children ?? [],
        { y: 40, opacity: 0, skewY: 2 },
        {
          y: 0,
          opacity: 1,
          skewY: 0,
          stagger: 0.1,
          duration: 0.7,
          ease: "power4.out",
        },
      ).fromTo(
        toolbarRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
        "-=0.3",
      );
    }, headerRef);
    return () => ctx.revert();
  }, []);

  /* Animate grid on filter transitions */
  const handleCategoryChange = useCallback((cat) => {
    const items = gridRef.current?.querySelectorAll("article") ?? [];
    gsap.to(items, {
      opacity: 0,
      y: 15,
      scale: 0.97,
      stagger: 0.04,
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => setActiveCategory(cat),
    });
  }, []);

  /* Smooth layout animations triggering right when dynamic array lengths sync */
  useEffect(() => {
    if (!gridRef.current || fetchedPosts.length === 0) return;
    const items = gridRef.current.querySelectorAll("article");
    gsap.fromTo(
      items,
      { opacity: 0, y: 20, scale: 0.97 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        stagger: 0.07,
        duration: 0.45,
        ease: "power3.out",
      },
    );
  }, [fetchedPosts.length, activeCategory]);

  return (
    <section
      className="relative overflow-hidden section-py"
      style={{
        background:
          "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)",
      }}
    >
      {/* BG Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 dot-pattern opacity-[0.05]" />
        <div
          className="absolute top-0 left-0 right-0 h-24"
          style={{
            background: "linear-gradient(to bottom, #f8fafc, transparent)",
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-24"
          style={{
            background: "linear-gradient(to top, #f8fafc, transparent)",
          }}
        />
        <div
          className="absolute top-1/3 left-1/4 w-80 h-80 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(29,78,216,0.07) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
      </div>

      <div className="container-fluid relative z-10">
        {/* ── HEADER ── */}
        <div ref={headerRef} className="mb-12 space-y-8">
          {/* Title text layout */}
          <div className="text-center max-w-2xl mx-auto space-y-4">
            <div ref={headRef} className="overflow-hidden space-y-1">
              <h2
                className="font-display font-black text-gray-900 leading-tight"
                style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
              >
                {blog.headline}
              </h2>
              <h2
                className="font-display font-black text-gradient-primary leading-tight"
                style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
              >
                {blog.headlineAccent}
              </h2>
            </div>
          </div>

          {/* Toolbar controllers layout */}
          <div ref={toolbarRef} className="space-y-4" style={{ opacity: 0 }}>
            {/* Input Element */}
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery("")}
            />

            {/* Category selection bar mapping layout changes */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CategoryBar
                categories={blog.categories}
                active={activeCategory}
                onChange={handleCategoryChange}
              />
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-600 font-mono hidden sm:block">
                  {totalItemsCount} articles
                </span>
                <LayoutToggle layout={layout} onChange={setLayout} />
              </div>
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="flex gap-8 xl:gap-12">
          {/* Articles grid content section */}
          <div className="flex-1 min-w-0">
            {status === "success" && fetchedPosts.length === 0 ? (
              /* Balanced custom design empty state view */
              <div className="text-center py-20 space-y-4">
                <div
                  className="w-16 h-16 rounded-2xl bg-primary-600/10
 border border-primary-600/20 flex items-center justify-center mx-auto"
                >
                  <Search size={24} className="text-primary-400" />
                </div>
                <p className="text-gray-900 font-semibold">No articles found</p>
                <p className="text-gray-600 text-sm">
                  Try adjusting your search or filter options.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("All");
                  }}
                  className="text-primary-400 text-sm hover:text-primary-300
 transition-colors duration-300 underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <div
                  ref={gridRef}
                  className={
                    layout === "grid"
                      ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
                      : "flex flex-col gap-4"
                  }
                >
                  {fetchedPosts.map((post, i) => (
                    <BlogCard
                      key={post.id}
                      post={post}
                      index={i}
                      layout={layout}
                    />
                  ))}
                </div>

                {/* Load more infinite scroll boundary trigger */}
                {hasNextPage && (
                  <div className="flex justify-center mt-10">
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="group inline-flex items-center gap-2.5
 px-8 py-4 border border-primary-500/40
 text-primary-300 hover:bg-primary-600/20
 hover:border-primary-500/60 font-semibold rounded-xl
 text-sm transition-all duration-300
 hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      <span>
                        {isFetchingNextPage
                          ? "Loading..."
                          : "Load More Articles"}
                      </span>
                      <ChevronRight
                        size={15}
                        className="rotate-90 transition-transform duration-300
 group-hover:translate-y-0.5"
                      />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Floating Content Sidebar Section */}
          <div className="hidden lg:block w-72 xl:w-80 flex-shrink-0">
            <BlogSidebar
              sidebar={{
                trendingTopics: serverTrendingTopics,
                popularTags: serverPopularTags,
              }}
              allPosts={fetchedPosts}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogGrid;
